import * as React from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { ILogin } from "../../vm";
import {
  isTokenExpired,
  setToken,
  setTokens,
  getQueryParams,
  parseJwt,
  passwordUpdatedDaysAgo,
  isValidPassword,
  isAdminUser,
} from "../../services/UtilService";
import { withToastContext, IToast } from "../common/ToastProvider";
import { mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import { Formik, FormikErrors } from "formik";
import {
  TextField,
  Stack,
  Text,
  PrimaryButton,
  Label,
  IIconProps,
  DefaultButton,
  Link,
} from "office-ui-fabric-react";
import { EMAIL_REGEX } from "../common/Constants";
import {
  loginToApp,
  changeExpiredPasswordForAdmin,
  changeExpiredPasswordForTeam,
  loginToAppWithMSOffice,
} from "../../services/auth/AuthService";
import "./login.css";
import {
  IPasswordChangeAlert,
  withPasswordChangeAlertContext,
} from "../change-password/PasswordChangeAlertProvider";
import PasswordPolicy from "../common/PasswordPolicy";
import LoginLeftSideImage from "../../images/login-image.png";
import LoginPageBg from "../../images/login-page-bg.png";
import {
  msalInstance,
  ensureMsalInitialized,
  msLoginRequest,
  MS_SSO_SUPPRESS_KEY,
  MS_SSO_LOGGED_OUT_PARAM,
  MS_SSO_MODE_KEY,
} from "../../authConfig";
import { AuthenticationResult } from "@azure/msal-browser";
import ForgotPassword from "./ForgotPassword";

export interface LoginMainProps
  extends RouteComponentProps,
    IToast,
    IPasswordChangeAlert {}

export interface LoginMainState {
  showPasswordUpdateModel?: boolean;
  token?: string;
  refreshToken?: string;
  showForgotPasswordModel?: boolean;
  // While true, render nothing so the login form never flashes before a
  // successful silent Microsoft SSO redirects the user away.
  isBootstrapping?: boolean;
}

class LoginMain extends React.Component<LoginMainProps, LoginMainState> {
  // Guards against React.StrictMode double-invocation in dev.
  private ssoStarted = false;

  constructor(props: LoginMainProps) {
    super(props);
    this.state = {
      showPasswordUpdateModel: false,
      token: undefined,
      refreshToken: undefined,
      showForgotPasswordModel: false,
      isBootstrapping: true,
    };
  }

  componentDidMount = async () => {
    if (this.ssoStarted) return;
    this.ssoStarted = true;

    // Already signed in → straight through.
    if (!isTokenExpired()) {
      this.props.history.push("/");
      await this.shouldShowPasswordChangeNotification();
      return;
    }

    try {
      await this.runSsoBootstrap();
    } catch (error) {
      console.error("SSO bootstrap failed:", error);
      this.setState({ isBootstrapping: false });
    }
  };

  // Deterministic login-page state machine for Microsoft SSO (redirect flow).
  runSsoBootstrap = async () => {
    await ensureMsalInitialized();

    const mode = sessionStorage.getItem(MS_SSO_MODE_KEY);

    // 1. Are we returning from a Microsoft redirect?
    let result = null;
    try {
      result = await msalInstance.handleRedirectPromise();
    } catch (err) {
      // A silent probe that actually needed interaction (no live MS session)
      // lands here. Suppress further probes and show the form.
      sessionStorage.removeItem(MS_SSO_MODE_KEY);
      if (mode === "silent") {
        sessionStorage.setItem(MS_SSO_SUPPRESS_KEY, "1");
      } else {
        this.props.showToast("Microsoft sign-in failed. Please try again.", "error");
      }
      this.setState({ isBootstrapping: false });
      return;
    }

    if (result && result.idToken) {
      if (mode) {
        sessionStorage.removeItem(MS_SSO_MODE_KEY);
        const idToken = await this.ensureFreshIdToken(result);
        await this.completeMsLogin(idToken, mode !== "interactive");
        return;
      }
      // A result with no silent/interactive marker means no redirect is
      // actually in flight: MSAL memoizes the redirect result for the lifetime
      // of the document, so reaching /login via an SPA route change (e.g.
      // AuthenticatedRoute's <Redirect> on session expiry) replays the ORIGINAL
      // login's result — a stale, likely expired ID token the backend would
      // reject. Ignore it and fall through to a fresh silent probe.
      console.warn("Ignoring memoized MSAL redirect result — no auth redirect in flight.");
    }

    // 2. Explicit logout, or a previously-failed silent probe → just show the form.
    const params: any = getQueryParams(window.location.href);
    const loggedOut = params[MS_SSO_LOGGED_OUT_PARAM];
    const suppressed = sessionStorage.getItem(MS_SSO_SUPPRESS_KEY);
    if (loggedOut || suppressed) {
      this.setState({ isBootstrapping: false });
      return;
    }

    // 3. Otherwise probe silently using the browser's Microsoft session
    //    (App Launcher case). Navigates away; if it succeeds we come back at (1).
    sessionStorage.setItem(MS_SSO_MODE_KEY, "silent");
    await msalInstance.loginRedirect({ ...msLoginRequest, prompt: "none" as const });
  };

  // Entra ID can return an ID token whose exp is anchored to the browser
  // session's ORIGINAL sign-in (ID tokens have a static ~60-min lifetime), so a
  // silent re-login more than an hour after first sign-in may come back already
  // expired even though it arrived fresh off the network — the backend would
  // reject it with TokenExpiredError. A refresh-token grant mints an ID token
  // with a fresh exp, so force one when needed.
  // See AzureAD/microsoft-authentication-library-for-js#4206.
  ensureFreshIdToken = async (result: AuthenticationResult): Promise<string> => {
    const claims: any = result.idTokenClaims || {};
    const skewMs = 60 * 1000; // refresh when expired or about to expire
    if (!claims.exp || claims.exp * 1000 > Date.now() + skewMs || !result.account) {
      return result.idToken;
    }
    try {
      const refreshed = await msalInstance.acquireTokenSilent({
        ...msLoginRequest,
        account: result.account,
        forceRefresh: true,
      });
      return refreshed.idToken;
    } catch (err) {
      console.error("Forced ID token refresh failed, using original token:", err);
      return result.idToken; // backend rejection then follows the normal failure path
    }
  };

  // Exchange the Microsoft ID token for this app's session.
  completeMsLogin = async (idToken: string, silent: boolean) => {
    const res = await loginToAppWithMSOffice(idToken);
    if (res?.success && res.data?.token) {
      sessionStorage.removeItem(MS_SSO_SUPPRESS_KEY);
      const tokenData = parseJwt(res.data.token);
      if (res.data.refreshToken) {
        setTokens(res.data.token, res.data.refreshToken);
      } else {
        setToken(res.data.token);
      }

      let params: any = getQueryParams(window.location.href);
      if (params["redirect_to"]) {
        this.props.history.push(params["redirect_to"]);
      } else if (
        tokenData.isAdmin === true &&
        (tokenData.role === 0 ||
          tokenData.role === 2 ||
          tokenData.role === 3 ||
          tokenData.role === 4 ||
          tokenData.role === 5)
      ) {
        this.props.history.push("/");
      } else {
        this.props.history.push(`/datacenters`);
      }
      return;
    }

    // Backend rejected (e.g. no matching account / disabled). Show the form +
    // reason; suppress so a silent probe doesn't immediately loop.
    if (silent) sessionStorage.setItem(MS_SSO_SUPPRESS_KEY, "1");
    this.props.showToast(res?.message || "Unable to sign in with Microsoft.", "error");
    this.setState({ isBootstrapping: false });
  };

  // "Sign in with Microsoft" button — interactive (shows the account picker).
  handleMicrosoftLogin = async () => {
    try {
      await ensureMsalInitialized();
      // The user is explicitly asking to sign in — lift any suppression left by
      // an earlier failed silent probe.
      sessionStorage.removeItem(MS_SSO_SUPPRESS_KEY);
      sessionStorage.setItem(MS_SSO_MODE_KEY, "interactive");
      await msalInstance.loginRedirect(msLoginRequest);
    } catch (err) {
      sessionStorage.removeItem(MS_SSO_MODE_KEY);
      this.props.showToast("Microsoft sign-in failed. Please try again.", "error");
    }
  };
  shouldShowPasswordChangeNotification = async () => {
    const days = await passwordUpdatedDaysAgo();
    // this.props.showPasswordChangeAlert({ isPolicyChangedUpdate: true });
    // this.props.showPasswordChangeAlert({ expireId: 14 });
    if (days !== null) {
      if (45 - days <= 15) {
        this.props.showPasswordChangeAlert({ expireId: 45 - days });
      }
    }
    if (days === null) {
      this.props.showPasswordChangeAlert({ isPolicyChangedUpdate: true });
    }
  };

  render() {
    if (this.state.isBootstrapping) return null;
    return (
      <React.Fragment>
        <main>
          <div className="left-section">
            {/* <div className="left-img-container"> */}
            <img src={LoginLeftSideImage} alt="" className="left-img" />
            {/* </div> */}
          </div>
          <div className="right-section">
            <div className="login-logo">
              <img
                src={"/logo.png"}
                alt="STTelemedia"
                style={{ height: 40, width: 210 }}
              />
            </div>
            <div className="login-card-section">
              {this.state.showPasswordUpdateModel ? (
                <section className="card login-card">
                  <Stack horizontalAlign="start">
                    {/* <Icon path={mdiLock} size="48px" /> */}
                    <Text className="sign-in-text">Update your Password</Text>
                  </Stack>

                  <Formik
                    initialValues={{ newPassword: "", confirmPassword: "" }}
                    validate={(values) => {
                      let errors: FormikErrors<{
                        newPassword?: string;
                        confirmPassword?: string;
                      }> = {};

                      if (!values.newPassword) {
                        errors.newPassword = "Required";
                      }
                      if (
                        values.newPassword &&
                        !isValidPassword(
                          values.newPassword,
                          isAdminUser(this.state.token)
                        )
                      ) {
                        errors.newPassword = "Please enter valid password.";
                      }
                      if (!values.confirmPassword) {
                        errors.confirmPassword = "Required";
                      }
                      if (
                        values.newPassword &&
                        values.confirmPassword &&
                        values.newPassword !== values.confirmPassword
                      ) {
                        errors.confirmPassword = "Password does not match";
                      }
                      return errors;
                    }}
                    onSubmit={async (
                      values: { newPassword: string; confirmPassword: string },
                      { setSubmitting, setFieldValue }
                    ) => {
                      const tokenData = parseJwt(this.state.token);
                      let result;
                      if (tokenData.isAdmin) {
                        result = await changeExpiredPasswordForAdmin(
                          values.newPassword,
                          this.state.token
                        );
                      } else {
                        result = await changeExpiredPasswordForTeam(
                          values.newPassword,
                          this.state.token
                        );
                      }
                      if (result && result.success) {
                        if (this.state.token && this.state.refreshToken) {
                          setTokens(this.state.token, this.state.refreshToken);
                        } else if (this.state.token) {
                          setToken(this.state.token);
                        }
                        this.props.showToast(
                          "Password updated successfully",
                          "success"
                        );
                        const params: any = getQueryParams(
                          window.location.href
                        );
                        console.log(
                          "params['redirect_to']",
                          params["redirect_to"]
                        );
                        if (params["redirect_to"]) {
                          this.props.history.push(params["redirect_to"]);
                        } else {
                          this.props.history.push("/");
                        }
                      } else {
                        this.props.showToast(result.message, "error");
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      /* and other goodies */
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Label className="login-label">New Password</Label>
                        <TextField
                          id="newPassword"
                          name="newPassword"
                          // label="New Password"
                          autoComplete="new-password"
                          type="password"
                          onChange={handleChange}
                          errorMessage={
                            errors.newPassword && touched.newPassword
                              ? errors.newPassword
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.newPassword}
                          underlined
                        />
                        {errors.newPassword &&
                          touched.newPassword &&
                          values.newPassword && (
                            <PasswordPolicy
                              password={values.newPassword}
                              isAdmin={isAdminUser(this.state.token)}
                            />
                          )}
                        <Label className="login-label">Confirm Password</Label>
                        <TextField
                          id="confirmPassword"
                          name="confirmPassword"
                          // label="Confirm Password"
                          autoComplete="confirm-password"
                          type="password"
                          onChange={handleChange}
                          errorMessage={
                            errors.confirmPassword && touched.confirmPassword
                              ? errors.confirmPassword
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.confirmPassword}
                          underlined
                        />
                        <br />

                        <PrimaryButton
                          type="submit"
                          disabled={isSubmitting}
                          className="login-btn"
                        >
                          Update Password
                        </PrimaryButton>
                      </form>
                    )}
                  </Formik>
                </section>
              ) : (
                <section className="card login-card">
                  <Stack horizontalAlign="start">
                    {/* <Icon path={mdiLock} size="48px" />
                    <Text variant="xLarge">STT Capacity Reading</Text>
                    <Text variant="xLarge">Sign In</Text> */}
                    <Text className="sign-in-text">Customer MOR SIGN IN</Text>
                  </Stack>
                  <Formik
                    initialValues={{ email: "", password: "" }}
                    validate={(values) => {
                      let errors: FormikErrors<ILogin> = {};
                      if (!values.email) {
                        errors.email = "Required";
                      } else if (!EMAIL_REGEX.test(values.email)) {
                        errors.email = "Invalid email address";
                      }
                      if (!values.password) {
                        errors.password = "Required";
                      } else if (values.password.length <= 4) {
                        errors.password = "Needs to be more than 4 characters";
                      }
                      return errors;
                    }}
                    onSubmit={async (values: ILogin, { setSubmitting }) => {
                      let res = await loginToApp(values);
                      if (res.success && res.data.token) {
                        // show toast message if has an active session
                        if (res.data.hasActiveSession) {
                          this.props.showToast(
                            "Login detected. Your previous session has been closed.",
                            "info"
                          );
                        }
                        const tokenData = parseJwt(res.data.token);
                        if (
                          tokenData &&
                          tokenData.passwordUpdatedByAdminAt != null
                        ) {
                          setSubmitting(false);
                          this.setState({
                            showPasswordUpdateModel: true,
                            token: res.data.token,
                            refreshToken: res.data.refreshToken || undefined,
                          });
                          return;
                        }

                        // Store both tokens
                        if (res.data.refreshToken) {
                          setTokens(res.data.token, res.data.refreshToken);
                        } else {
                          setToken(res.data.token); // Fallback for backward compatibility
                        }
                        // check if needs to show password change notification
                        await this.shouldShowPasswordChangeNotification();

                        let params: any = getQueryParams(window.location.href);
                        if (params["redirect_to"]) {
                          this.props.history.push(params["redirect_to"]);
                        } else {
                          if (
                            tokenData.isAdmin === true &&
                            (tokenData.role === 0 ||
                              tokenData.role === 2 ||
                              tokenData.role === 3 ||
                              tokenData.role === 4 ||
                              tokenData.role === 5)
                          ) {
                            this.props.history.push("/");
                          } else {
                            this.props.history.push(
                              `/datacenters`
                              // `/datacenters/${tokenData.datacenterId}`
                            );
                          }
                        }
                      } else {
                        this.props.showToast(res?.message, "error");
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      /* and other goodies */
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Label className="login-label">Email Address</Label>
                        <TextField
                          id="email"
                          name="email"
                          // label="Email Address"
                          autoComplete="email"
                          autoFocus
                          onChange={handleChange}
                          errorMessage={
                            errors.email && touched.email ? errors.email : ""
                          }
                          onBlur={handleBlur}
                          value={values.email}
                          underlined
                        />
                        <Label className="login-label">Password</Label>
                        <TextField
                          id="password"
                          name="password"
                          // label="Password"
                          type="password"
                          autoComplete="current-password"
                          onChange={handleChange}
                          errorMessage={
                            errors.password && touched.password
                              ? errors.password
                              : ""
                          }
                          onBlur={handleBlur}
                          value={values.password}
                          underlined
                        />
                        <br />
                        <PrimaryButton
                          type="submit"
                          disabled={isSubmitting}
                          className="login-btn"
                        >
                          SIGN IN
                        </PrimaryButton>
                        <div style={{ display: "flex", justifyContent: "end" }}>
                          <Link
                            className="ms-Button-label forgot-password-btn"
                            disabled={isSubmitting}
                            onClick={() =>
                              this.setState({ showForgotPasswordModel: true })
                            }
                          >
                            Forgot Password?
                          </Link>
                        </div>
                      </form>
                    )}
                  </Formik>
                  <div className="sso-divider">
                    <span>or</span>
                  </div>
                  <DefaultButton
                    className="office-btn"
                    onClick={this.handleMicrosoftLogin}
                  >
                    <span
                      className="ms-logo"
                      aria-hidden="true"
                      style={{ display: "inline-flex", marginRight: 8 }}
                    >
                      <svg width="18" height="18" viewBox="0 0 21 21">
                        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                      </svg>
                    </span>
                    Sign in with Microsoft
                  </DefaultButton>
                </section>
              )}
              <div className="login-bg-image">
                <img src={LoginPageBg} alt="login-bg" />
              </div>
            </div>
          </div>
          {this.state.showForgotPasswordModel && (
            <ForgotPassword
              onClose={() => this.setState({ showForgotPasswordModel: false })}
            />
          )}
        </main>
      </React.Fragment>
    );
  }
}

export default withToastContext(
  withPasswordChangeAlertContext(withRouter(LoginMain))
);
